import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, Loader2 } from 'lucide-react';
import { RefundNotice } from '@/components/digital/RefundNotice';
import { digitalApi } from '@/lib/digitalApi';
import { usePageMeta } from '@/lib/usePageMeta';

export default function DigitalProductThankYou() {
  usePageMeta({ title: 'Thank You', path: '/resources/digital-products/thank-you', robots: 'noindex' });
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (!sessionId) { setState({ error: 'Missing checkout session.' }); return undefined; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await digitalApi.orderStatus(sessionId);
        setState(r);
        if (!r.paid && tries < 10) { tries += 1; timer = setTimeout(poll, 3000); }
      } catch {
        setState({ error: 'We could not find this checkout session.' });
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="bg-ivory min-h-screen" data-testid="digital-thank-you-page">
      <section className="pt-40 lg:pt-56 pb-24">
        <div className="mx-auto max-w-3xl px-6 sm:px-10">
          <div className="eyebrow !text-gold mb-8">ORIGENNE&trade; &middot; Digital Products</div>
          {state.error && (
            <>
              <h1 className="font-serif-display text-5xl tracking-tighter text-matte">Something&rsquo;s not right.</h1>
              <p className="mt-6 text-matte/70" data-testid="digital-thank-you-error">{state.error}</p>
              <Link to="/resources/digital-products" className="btn-primary mt-10">Back to the Library</Link>
            </>
          )}
          {!state.error && !state.paid && (
            <>
              <h1 className="font-serif-display text-5xl md:text-6xl tracking-tighter text-matte">Confirming your payment&hellip;</h1>
              <p className="mt-6 text-matte/70 flex items-center gap-3" data-testid="digital-thank-you-pending">
                <Loader2 size={16} className="animate-spin" /> This usually takes a few seconds. Your download link will also be emailed to you.
              </p>
            </>
          )}
          {state.paid && (
            <>
              <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.98] tracking-tightest text-matte">
                Thank you. <em className="italic text-taupe">It&rsquo;s yours.</em>
              </h1>
              <p className="mt-8 text-matte/70 leading-relaxed" data-testid="digital-thank-you-paid">
                Your payment for <strong className="text-matte">{state.product_title}</strong> is confirmed.
                A secure download link has been sent to {state.email_masked}.
              </p>
              {state.download_path && (
                <Link to={state.download_path} className="btn-primary mt-10" data-testid="digital-thank-you-download">
                  <Download size={14} strokeWidth={1.6} /> Go to Download
                </Link>
              )}
              <RefundNotice className="mt-16" />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
